import React, { useEffect, useState } from "react";
import { api } from "../api.js";
import { Spinner, Toggle } from "../components/ui.js";
import { capture } from "../lib/posthog.js";

export interface RewritePaneProps {
  projectId: string;
  /** Called once the server has rebuilt the timeline. */
  onRebuilt: () => void;
  /**
   * The cut, when there is one, so the way back lives beside the way in.
   *
   * Restoring used to be a second toolbar button that only meant anything once
   * a cut existed, sitting beside the one that made it. Both are the same
   * decision, so they belong in the same place.
   */
  autoCut?: {
    enabled: boolean;
    removedSeconds: number;
    takes: number;
    busy: boolean;
    onChange: (value: boolean) => void;
    /** Re-plans the cut after the original voice is switched. Absent when nothing is cut. */
    onVoiceChanged?: (voiceOn: boolean) => void;
  };
}

/**
 * Rewriting the narration, with the prompt on the table.
 *
 * The prompt is the whole of what this feature does, and hiding it made the
 * button a slot machine: press it, get different words, with no way to say what
 * you actually wanted. Showing the real text — fetched from the server, not a
 * copy that can drift from it — turns "try again" into "change this line".
 *
 * Edits are remembered per project, because someone who has tuned the wording
 * for their product wants it next time too.
 */
export const RewritePane: React.FC<RewritePaneProps> = ({ projectId, onRebuilt, autoCut }) => {
  const [defaultPrompt, setDefaultPrompt] = useState("");
  const [prompt, setPrompt] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [voice, setVoice] = useState(true);
  const [tighten, setTighten] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void api
      .scriptPrompt(projectId)
      .then((result) => {
        if (cancelled) return;
        setDefaultPrompt(result.default);
        setPrompt(result.using);
        setLoaded(true);
      })
      .catch((cause: Error) => setError(cause.message));
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  const edited = loaded && prompt.trim() !== defaultPrompt.trim();

  const run = async () => {
    // Tightening leads with the cut now, so the label has to as well.
    setBusy(tighten ? "Tightening" : "Rewriting");
    setError(null);
    setNote(null);
    try {
      const { jobId } = await api.regenerateScript(projectId, {
        // Only send the prompt when it differs, so an untouched panel does not
        // freeze today's default onto the project for ever.
        prompt: edited ? prompt : "",
        voice,
        tighten,
      });
      capture("script_rewrite_requested", {
        project_id: projectId,
        voice_enabled: voice,
        tighten_recording: tighten,
        prompt_customized: edited,
      });

      // The rewrite queues the follow-on work, so waiting on this one job only
      // covers the first step. The label tracks whatever is running.
      const deadline = Date.now() + 30 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const job = await api.jobStatus(projectId, jobId).catch(() => null);
        if (!job?.finished) continue;
        if (job.status === "failed") throw new Error(job.error ?? "The rewrite failed.");
        setNote(job.note);
        break;
      }

      // The follow-on work runs as its own jobs, and the project's status is the
      // honest signal for "still working". With tightening the chain is cut,
      // then rewrite, then voice if it was asked for — so what comes after the
      // first job is the rewrite, not the cut that has just finished.
      while (Date.now() < deadline && (voice || tighten)) {
        await new Promise((resolve) => setTimeout(resolve, 2000));
        const status = await api.status(projectId).catch(() => null);
        if (!status) continue;
        if (status.status === "failed") throw new Error(status.error ?? "The follow-on step failed.");
        if (status.status === "ready" || status.status === "completed") break;
        setBusy(tighten ? "Rewriting" : "Generating voice");
      }

      onRebuilt();
      setNote((current) => current ?? "Done.");
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[11px] leading-relaxed text-ink-400">
        The instructions the model is given, alongside your transcript. Edit them and they are kept for this project.
      </p>

      <div className="flex flex-col gap-1.5">
        <div className="flex items-baseline justify-between">
          <span className="text-[11px] font-medium text-ink-300">Prompt</span>
          {edited ? (
            <button
              onClick={() => setPrompt(defaultPrompt)}
              className="text-[11px] text-ink-400 underline-offset-2 hover:text-ink-100 hover:underline"
            >
              reset to default
            </button>
          ) : null}
        </div>
        <textarea
          className="input min-h-[220px] resize-y font-mono text-[11px] leading-relaxed"
          value={prompt}
          spellCheck={false}
          disabled={!loaded || busy !== null}
          onChange={(event) => setPrompt(event.target.value)}
        />
      </div>

      <Toggle
        label="Generate the voice after"
        checked={voice}
        hint="New words need new audio; without this the narration stays as it was."
        onChange={setVoice}
      />

      <Toggle
        label="Tighten the recording first"
        checked={tighten}
        hint="Cuts the stretches where nothing is said or clicked, then writes the narration for what is left."
        onChange={setTighten}
      />

      {/* Said plainly because it is the one option here that changes the
          picture rather than the words. Rewriting on its own never shortens the
          recording — the video's length comes from the file — so a script that
          says less just leaves more silence. This is what closes that gap, and
          it is the same auto-cut as the Tighten button, with the same undo. */}
      {tighten ? (
        <p className="rounded-[5px] border border-[var(--edge)] bg-bay-850 p-2.5 text-[11px] leading-relaxed text-ink-300">
          This shortens the video. Stretches with nothing said and nothing clicked are removed, so the finished
          recording will be shorter than the one you uploaded. The cut is a list of what to skip, not a new file — your
          original recording is never touched, and you can put the full length back below.
        </p>
      ) : null}

      <button className="btn btn-primary btn-sm" disabled={!loaded || busy !== null} onClick={() => void run()}>
        {busy ? (
          <>
            <Spinner className="!h-3 !w-3" /> {busy}…
          </>
        ) : (
          "AI Rewrite"
        )}
      </button>

      <p className="text-[11px] leading-relaxed text-ink-400">
        Rewriting replaces the whole script, including any lines you have edited by hand.
      </p>

      {error ? <p className="text-[11px] leading-snug text-signal-bad">{error}</p> : null}
      {note ? <p className="text-[11px] leading-snug text-signal-ok">{note}</p> : null}

      {/* Only once there is something to restore. A control that spends most of
          its life doing nothing teaches people to stop reading it. */}
      {autoCut?.enabled ? (
        <div className="flex flex-col gap-2 border-t border-[var(--edge)] pt-4">
          <span className="text-[11px] leading-relaxed text-ink-300">
            {Math.round(autoCut.removedSeconds)}s of dead air removed, across {autoCut.takes}{" "}
            {autoCut.takes === 1 ? "take" : "takes"}.
          </span>
          {/* A bordered button, not a ghost one. Undoing the cut is a real
              action on the recording, and drawn as bare text it read as a
              caption under the sentence above it — the one control here that
              puts your footage back looked like part of the explanation. */}
          <button
            className="btn btn-secondary btn-sm self-start"
            disabled={autoCut.busy || busy !== null}
            onClick={() => autoCut.onChange(false)}
            title="Put the whole recording back. The narration is not rewritten, so it will describe the restored footage again."
          >
            {autoCut.busy ? "Restoring…" : "Restore full length"}
          </button>
        </div>
      ) : null}
    </div>
  );
};
